import {
  Captions,
  Check,
  CircleCheck,
  CircleHelp,
  ClipboardCheck,
  FastForward,
  Film,
  FolderOpen,
  Keyboard,
  MessageSquare,
  Pause,
  Play,
  Rewind,
  RotateCcw,
  StickyNote,
  Trash2,
  Type,
  Volume2,
  X,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import Linkify from "react-linkify";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { MediaPlayer, MediaPlayerHandle } from "@/components/MediaPlayer";
import { Button, ButtonGroup, confirmDialog, Dialog, Kbd, KbdGroup, Textarea, Tooltip } from "@/components/ui";
import { useInvalidateVideoProjectEventLogs } from "@/containers/Editor/VideoProjectPage/components/VideoProjectHistoryPanel";
import { cn } from "@/lib/utils";
import { EDITOR_VIDEO_PROJECT_EXTRA_FIELDS, Media, VideoProject, VideoProjectServices } from "@/services/bedrock";
import { editorQueueListUrl, getErrorMessage, printDateToChat } from "@/utils";
import { StylePreferencesOptions } from "@/utils/projectHelpers";

import {
  buildQaIssues,
  createQaFlagId,
  formatQaTimestamp,
  LastFailResolution,
  QaFlag,
  unmarkedLastFailIssues,
  unresolvedLastFailIssues,
} from "./qaIssues";
import {
  categoryFromShortcut,
  clampPlayheadSeconds,
  clipForQaIssue,
  hasReadableNotes,
  isTypingTarget,
  isWatchComplete,
  isVideoMedia,
  flagBelongsToClip,
  extraQaStudioClips,
  lastRejectedAudit,
  nextQaStudioClip,
  qaStudioClips,
  QaStudioClip,
  QaStudioSource,
  PLAYBACK_RATES,
  PlaybackRate,
  QA_SKIP_SECONDS,
  stackSkipCueSeconds,
  spokenLinesFromMedia,
  srtCuesFromMedia,
  activeComparisonRowIndex,
  activeTimedLineIndex,
  activeTimedPartIndex,
  comparisonDifferenceCount,
  comparisonRowAsTimedLine,
  timedPartAsTimedLine,
  hasTranscriptPollChange,
  shouldPollEditedSpokenTranscripts,
  shouldPollTranscriptStatus,
  shouldShowOnScreenStack,
  shouldShowSpokenStack,
  shouldShowTranscriptComparison,
  shouldShowTranscriptRail,
  seekSecondsForTimedLine,
  scrollTopToCenterElement,
  clampScrollTop,
  DiffPart,
  TimedLine,
  TranscriptComparisonRow,
  transcriptComparisonFromMedia,
  wordingDifferenceCount,
} from "./qaStudio";

const EMPTY_QA_AUDIT_ISSUES: VideoProject.QaAuditIssue[] = [];

const LENS_ICONS: Record<VideoProject.QaIssueCategory, LucideIcon> = {
  ON_SCREEN_TEXT: Type,
  POST_AND_COMPLIANCE: Captions,
  B_ROLL_GRAPHICS: Film,
  OPEN_AND_CLOSE: ClipboardCheck,
  SOUND_AND_PACING: Volume2,
  OTHER: StickyNote,
};

const LENS_SPOTLIGHT: Record<VideoProject.QaIssueCategory, string> = {
  ON_SCREEN_TEXT: "text",
  POST_AND_COMPLIANCE: "post",
  B_ROLL_GRAPHICS: "source",
  OPEN_AND_CLOSE: "openClose",
  SOUND_AND_PACING: "spec",
  OTHER: "notes",
};

interface VideoProjectQaStudioProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoProject: VideoProject;
  setVideoProject: React.Dispatch<React.SetStateAction<VideoProject | undefined>>;
  playerNotes?: VideoProject.ClientNote;
  coachNotes?: VideoProject.ClientNote;
  preferencesInfo: StylePreferencesOptions[];
  formatHexColor: (color: string) => string | null;
  videoProjectMessages?: VideoProject.Message[];
  finalMessage: string;
  hashtags: string;
  blockedWordsInCaption?: string;
  readOnly?: boolean;
}

export function VideoProjectQaStudio({
  open,
  onOpenChange,
  videoProject,
  setVideoProject,
  playerNotes,
  coachNotes,
  preferencesInfo,
  formatHexColor,
  videoProjectMessages,
  finalMessage,
  hashtags,
  blockedWordsInCaption,
  readOnly = false,
}: VideoProjectQaStudioProps) {
  const history = useHistory();
  const invalidateEventLogs = useInvalidateVideoProjectEventLogs();
  const playerRef = React.useRef<MediaPlayerHandle>(null);
  const commentRef = React.useRef<HTMLTextAreaElement>(null);
  const pendingSeekRef = React.useRef<number | null>(null);
  const isMac = React.useMemo(() => {
    if (typeof window === "undefined") return true;
    return /Macintosh|Mac OS X/i.test(navigator.userAgent);
  }, []);
  const modKey = isMac ? "⌘" : "Ctrl";

  const [playbackRate, setPlaybackRate] = React.useState<PlaybackRate>(1);
  const [playing, setPlaying] = React.useState(false);
  const [currentSeconds, setCurrentSeconds] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [maxPlayed, setMaxPlayed] = React.useState(0);
  const [skippedWatch, setSkippedWatch] = React.useState(false);
  const [selectedClipKey, setSelectedClipKey] = React.useState<string | null>(null);
  const [flags, setFlags] = React.useState<QaFlag[]>([]);
  const [lastFailResolutions, setLastFailResolutions] = React.useState<Record<number, LastFailResolution | undefined>>(
    {}
  );
  const [selectedFlagId, setSelectedFlagId] = React.useState<string | null>(null);
  const [spotlight, setSpotlight] = React.useState<VideoProject.QaIssueCategory | null>(null);
  const [showShortcuts, setShowShortcuts] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [playbackCue, setPlaybackCue] = React.useState<{ id: number; playing: boolean } | null>(null);
  const [skipCue, setSkipCue] = React.useState<{ id: number; at: number; seconds: number } | null>(null);
  const [retryingSpoken, setRetryingSpoken] = React.useState(false);

  const setPlayingWithCue = React.useCallback((next: boolean | ((current: boolean) => boolean)) => {
    setPlaying(current => {
      const resolved = typeof next === "function" ? next(current) : next;
      if (resolved !== current) {
        setPlaybackCue({ id: Date.now(), playing: resolved });
      }
      return resolved;
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setFlags([]);
    setLastFailResolutions({});
    setSelectedFlagId(null);
    setSpotlight(null);
    setSelectedClipKey(null);
    setSkippedWatch(false);
    setMaxPlayed(0);
    setCurrentSeconds(0);
    setPlaying(false);
    setShowShortcuts(false);
    setPlaybackCue(null);
    setSkipCue(null);
    pendingSeekRef.current = null;
  }, [open, videoProject.id]);

  const clips = React.useMemo(
    () => qaStudioClips(videoProject.editedMedia, videoProject.originalMedia),
    [videoProject.editedMedia, videoProject.originalMedia]
  );
  const lastFail = lastRejectedAudit(videoProject.qaAudits);
  const lastFailIssues = lastFail?.qaAuditIssues ?? EMPTY_QA_AUDIT_ISSUES;
  const extraClips = React.useMemo(() => extraQaStudioClips(videoProject), [videoProject.lastApprovedMedia]);
  const lastApprovedClip = extraClips.find(clip => clip.source === "LAST_APPROVED");
  const selectedClip =
    extraClips.find(clip => clip.key === selectedClipKey) ??
    clips.find(clip => clip.key === selectedClipKey) ??
    clips.find(clip => clip.source === "EDITED") ??
    clips[0];
  const activeMedia = selectedClip?.media;
  const canFlag = !readOnly && selectedClip?.source === "EDITED";
  const selectedAppearance = selectedClip ? clipAppearance(selectedClip.source) : undefined;
  const caption = [finalMessage, hashtags].filter(Boolean).join(" ");
  const shouldPollSpoken = shouldPollEditedSpokenTranscripts(videoProject.editedMedia);
  const issues = buildQaIssues(flags);
  const unmarkedLastFail = unmarkedLastFailIssues(lastFailIssues, lastFailResolutions);
  const unresolvedLastFail = unresolvedLastFailIssues(lastFailIssues, lastFailResolutions);
  const hasIssues = issues.length > 0 || unresolvedLastFail.length > 0;
  const watchComplete = isWatchComplete(maxPlayed, skippedWatch);
  const player = videoProject.player;
  const playerInfoRows = [
    { label: "Name", value: player?.fullName || videoProject.playerName },
    { label: "Aliases", value: player?.aliases?.filter(Boolean).join(", ") },
    { label: "Company", value: videoProject.companyName },
    { label: "Phone", value: player?.phone },
    { label: "Email", value: player?.email },
    { label: "Website", value: player?.webAddress },
    { label: "Regions", value: player?.regionNames?.filter(Boolean).join(", ") },
    { label: "Branches", value: player?.branchNames?.filter(Boolean).join(", ") },
    { label: "Role", value: player?.industryRoles?.[0]?.name },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));

  const mergeTranscriptMedia = React.useCallback(
    (latest: VideoProject) => {
      setVideoProject(prev => {
        if (!prev || !hasTranscriptPollChange(prev, latest)) return prev;
        return {
          ...prev,
          editedMedia: latest.editedMedia ?? prev.editedMedia,
        };
      });
    },
    [setVideoProject]
  );

  React.useEffect(() => {
    if (!open || !shouldPollSpoken) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const latest = await VideoProjectServices.show(videoProject.id, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
        if (!cancelled) mergeTranscriptMedia(latest);
      } catch {
        // Keep reviewing even if transcript polling fails.
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [mergeTranscriptMedia, open, shouldPollSpoken, videoProject.id]);

  const applyUpdatedProject = (updated: VideoProject) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: updated.status ?? prev.status,
        editorUserId: updated.editorUserId ?? prev.editorUserId,
        qaUserId: updated.qaUserId ?? prev.qaUserId,
        qaStatus: updated.qaStatus ?? prev.qaStatus,
        qaDate: updated.qaDate ?? prev.qaDate,
        qaAudits: updated.qaAudits ?? prev.qaAudits,
      };
    });
    invalidateEventLogs(videoProject.id);
  };

  const runEvent = async (event: VideoProject.Event, nextIssues?: VideoProject.QaIssue[]) => {
    setSaving(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event,
        ...(nextIssues ? { issues: nextIssues } : {}),
      });
      applyUpdatedProject(updated);
      return updated;
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update project");
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const seekTo = React.useCallback(
    (seconds: number) => {
      const next = clampPlayheadSeconds(seconds, duration || activeMedia?.duration || 0);
      playerRef.current?.seekTo(next);
      setCurrentSeconds(next);
    },
    [duration, activeMedia?.duration]
  );

  const seekToTimedLine = React.useCallback(
    (line: TimedLine) => {
      seekTo(seekSecondsForTimedLine(line));
      setPlayingWithCue(true);
    },
    [seekTo, setPlayingWithCue]
  );

  const retrySpokenTranscript = React.useCallback(async () => {
    if (retryingSpoken) return;
    setRetryingSpoken(true);
    try {
      await VideoProjectServices.retryEditedTranscript(videoProject.id);
      const latest = await VideoProjectServices.show(videoProject.id, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
      mergeTranscriptMedia({
        ...latest,
        editedMedia: (latest.editedMedia || videoProject.editedMedia || []).map(media => ({
          ...media,
          transcriptStatus: media.transcriptStatus === "FAILED" ? "WAITING" : media.transcriptStatus,
        })),
      });
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to retry spoken transcript");
    } finally {
      setRetryingSpoken(false);
    }
  }, [mergeTranscriptMedia, retryingSpoken, videoProject.editedMedia, videoProject.id]);

  const skipBy = React.useCallback(
    (requestedDelta: number) => {
      const durationSeconds = duration || activeMedia?.duration || 0;
      const next = clampPlayheadSeconds(currentSeconds + requestedDelta, durationSeconds);
      const actualDelta = next - currentSeconds;
      seekTo(next);
      setSkipCue(current => {
        const now = Date.now();
        const seconds = stackSkipCueSeconds(current, actualDelta, now, requestedDelta);
        if (seconds == null) return current;
        return { id: now, at: now, seconds };
      });
    },
    [activeMedia?.duration, currentSeconds, duration, seekTo]
  );

  const selectClip = React.useCallback((clip: QaStudioClip, seekSeconds?: number) => {
    pendingSeekRef.current = seekSeconds ?? null;
    setSelectedClipKey(clip.key);
    setCurrentSeconds(seekSeconds ?? 0);
    setDuration(0);
    setPlaying(false);
  }, []);

  const handleDuration = React.useCallback((nextDuration: number) => {
    setDuration(nextDuration);
    const pending = pendingSeekRef.current;
    if (pending == null) return;
    pendingSeekRef.current = null;
    const next = clampPlayheadSeconds(pending, nextDuration);
    playerRef.current?.seekTo(next);
    setCurrentSeconds(next);
  }, []);

  const focusIssue = React.useCallback(
    (issue: { timestampMs?: number | null }) => {
      const clip = clipForQaIssue(clips);
      const seekSeconds = issue.timestampMs != null ? issue.timestampMs / 1000 : undefined;
      if (clip && clip.key !== selectedClip?.key) {
        selectClip(clip, seekSeconds);
        return;
      }
      if (seekSeconds != null) seekTo(seekSeconds);
    },
    [clips, selectClip, seekTo, selectedClip?.key]
  );

  const addFlag = React.useCallback(
    (category: VideoProject.QaIssueCategory) => {
      if (!canFlag) return;
      const flag: QaFlag = {
        id: createQaFlagId(),
        category,
        comment: "",
        timestampMs: Math.round(currentSeconds * 1000),
      };
      setFlags(current => [...current, flag]);
      setSelectedFlagId(flag.id);
      setSpotlight(category);
      window.setTimeout(() => commentRef.current?.focus(), 0);
    },
    [canFlag, currentSeconds]
  );

  const removeFlag = (id: string) => {
    setFlags(current => current.filter(flag => flag.id !== id));
    setSelectedFlagId(current => (current === id ? null : current));
  };

  const handleSubmit = async () => {
    if (readOnly || saving) return;
    if (unmarkedLastFail.length) {
      toast.error("Mark each previous QA issue as resolved or not.");
      return;
    }
    const sendingBack = hasIssues;
    const nextIssues = [...issues, ...unresolvedLastFail];
    const watchedPercent = Math.round(maxPlayed * 100);
    const confirmed = await confirmDialog({
      title: sendingBack ? "Send back for edits?" : "Send to client?",
      description: (
        <div className="space-y-2 text-sm text-slate-600">
          <p>
            {sendingBack
              ? `This will send ${nextIssues.length} issue${nextIssues.length === 1 ? "" : "s"} back to the editor${
                  unresolvedLastFail.length
                    ? `, including ${unresolvedLastFail.length} still unresolved from the last review`
                    : ""
                }.`
              : "This will mark QA as approved and send the project to the client."}
          </p>
          {!watchComplete ? <p>You have not finished watching the cut ({watchedPercent}% watched).</p> : null}
        </div>
      ),
      confirmLabel: sendingBack ? "Send back for edits" : "Send to client",
      cancelLabel: "Keep reviewing",
      destructive: sendingBack,
    });
    if (!confirmed) return;
    if (!watchComplete) setSkippedWatch(true);

    if (sendingBack) {
      const updated = await runEvent(VideoProject.Event.REJECT_QA, nextIssues);
      if (!updated) return;
      toast.success("Project sent back for edits");
      onOpenChange(false);
      history.replace(editorQueueListUrl());
      return;
    }

    const updated = await runEvent(VideoProject.Event.APPROVE_QA);
    if (!updated) return;
    toast.success("Project sent to the client");
    onOpenChange(false);
  };

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);
      if (!typing && (event.key === "?" || (event.key === "/" && event.shiftKey))) {
        event.preventDefault();
        setShowShortcuts(current => !current);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        if (showShortcuts) {
          setShowShortcuts(false);
          return;
        }
        if (selectedFlagId && typing) {
          (event.target as HTMLElement).blur();
          return;
        }
        if (selectedFlagId) {
          const flag = flags.find(item => item.id === selectedFlagId);
          if (flag && !flag.comment.trim()) removeFlag(flag.id);
          else setSelectedFlagId(null);
          return;
        }
        return;
      }
      const withMod = isMac ? event.metaKey : event.ctrlKey;
      if (withMod && event.key === "Enter") {
        if (readOnly) return;
        event.preventDefault();
        void handleSubmit();
        return;
      }
      if (typing) return;

      if (event.key === " " || event.key === "k" || event.key === "K") {
        event.preventDefault();
        setPlayingWithCue(current => !current);
        return;
      }
      if (event.key === "j" || event.key === "J" || event.key === "ArrowLeft") {
        event.preventDefault();
        skipBy(-QA_SKIP_SECONDS);
        return;
      }
      if (event.key === "l" || event.key === "L" || event.key === "ArrowRight") {
        event.preventDefault();
        skipBy(QA_SKIP_SECONDS);
        return;
      }
      if (event.key === "i" || event.key === "I") {
        event.preventDefault();
        const next = nextQaStudioClip(clips, selectedClip?.key);
        if (next) selectClip(next);
        return;
      }
      if ((event.key === "Backspace" || event.key === "Delete") && selectedFlagId) {
        if (readOnly) return;
        event.preventDefault();
        removeFlag(selectedFlagId);
        return;
      }
      const category = categoryFromShortcut(event.key);
      if (category && canFlag && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
        event.preventDefault();
        addFlag(category);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const spotlightRegion = spotlight ? LENS_SPOTLIGHT[spotlight] : null;
  const durationSeconds = duration || activeMedia?.duration || 0;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="full"
      showCloseButton
      disableEscapeDismissal
      className="flex h-[calc(100vh-2rem)] max-h-[100vh] min-h-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl"
    >
      <Dialog.Header className="shrink-0 px-5 py-3">
        <div className="flex items-center justify-between gap-4 pr-8">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
              <ClipboardCheck className="size-4" />
            </div>
            <Dialog.Title className="min-w-0 text-base font-bold text-slate-800">
              QA review · {videoProject.playerName || "Player"}
            </Dialog.Title>
            {readOnly ? (
              <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                View only
              </span>
            ) : null}
          </div>
        </div>
      </Dialog.Header>

      <Dialog.Content className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 lg:grid-cols-[18rem_minmax(0,1fr)_20rem]">
        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto">
          <div className="flex flex-col gap-1.5">
            {VideoProject.QaIssueCategories.map(category => {
              const meta = VideoProject.QaIssueCategoryMeta[category];
              const Icon = LENS_ICONS[category];
              const count = flags.filter(flag => flag.category === category).length;
              const active = spotlight === category;
              return (
                <Tooltip key={category}>
                  <Tooltip.Trigger
                    render={
                      <button
                        type="button"
                        onClick={() => {
                          if (readOnly || !canFlag) {
                            setSpotlight(current => (current === category ? null : category));
                            return;
                          }
                          addFlag(category);
                        }}
                        onMouseEnter={() => setSpotlight(category)}
                        onMouseLeave={() => setSpotlight(current => (current === category ? null : current))}
                        className={cn(
                          "rounded-xl border px-3 py-2 text-left transition-all",
                          !readOnly && !canFlag && "cursor-not-allowed opacity-50",
                          active
                            ? "border-amber-400 bg-amber-50"
                            : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/60 hover:shadow-sm"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex size-6 items-center justify-center rounded bg-slate-100 font-mono text-[11px] text-slate-600">
                            {meta.shortcut}
                          </span>
                          <Icon className="size-3.5 text-slate-400" />
                          <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800">{meta.label}</span>
                          <CircleHelp className="size-3.5 shrink-0 text-slate-400" />
                          {count ? <span className="text-[11px] font-semibold text-amber-600">{count}</span> : null}
                        </div>
                      </button>
                    }
                  />
                  <Tooltip.Content side="right" align="start" className="max-w-72 px-3 py-2.5">
                    <p className="mb-1.5 font-semibold">Common things to look for</p>
                    <ul className="space-y-1 text-left font-normal">
                      {meta.lookFor.map(item => (
                        <li key={item} className="flex gap-1.5">
                          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-background/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </Tooltip.Content>
                </Tooltip>
              );
            })}
            {readOnly ? (
              <p className="text-[11px] text-slate-400">Hover a category to spotlight matching evidence.</p>
            ) : !canFlag ? (
              <p className="text-[11px] text-slate-400">Switch to the edited video to flag issues.</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">Flagged</span>
              <span className="text-[11px] text-slate-400">
                {readOnly
                  ? lastFailIssues.length || flags.length
                    ? `${lastFailIssues.length + flags.length} issues`
                    : "No issues"
                  : unmarkedLastFail.length
                  ? "Mark each last fail"
                  : hasIssues
                  ? `${flags.length + unresolvedLastFail.length} issues`
                  : "Looking good"}
              </span>
            </div>
            {lastFailIssues.map(issue => {
              const resolution = lastFailResolutions[issue.id];
              return (
                <div
                  key={`last-fail-${issue.id}`}
                  className={cn(
                    "rounded-lg border px-2 py-1.5 text-xs",
                    resolution == null
                      ? readOnly
                        ? "border-rose-200 bg-rose-50"
                        : "border-amber-400 bg-amber-50"
                      : resolution === "UNRESOLVED"
                      ? "border-rose-200 bg-rose-50"
                      : "border-slate-200 bg-white"
                  )}
                >
                  <button
                    type="button"
                    className="flex w-full cursor-pointer items-start justify-between gap-2 text-left"
                    onClick={() => focusIssue(issue)}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 font-medium text-slate-800">
                        <span className="rounded bg-rose-600 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-white">
                          Last fail
                        </span>
                        <span>{VideoProject.qaIssueCategoryMeta(issue.category).label}</span>
                      </span>
                      {issue.comment ? <p className="mt-0.5 text-slate-500">{issue.comment}</p> : null}
                    </span>
                    <span className="shrink-0 text-slate-400">
                      {formatQaTimestamp(issue.timestampMs) || "Whole cut"}
                    </span>
                  </button>
                  {readOnly ? null : (
                    <ButtonGroup className="mt-1.5">
                      <Button
                        type="button"
                        size="xs"
                        variant={resolution === "RESOLVED" ? "default" : "outline"}
                        className={cn(
                          "h-6 px-2 text-[11px]",
                          resolution === "RESOLVED" && "bg-emerald-500 text-white hover:bg-emerald-400"
                        )}
                        onClick={() => setLastFailResolutions(current => ({ ...current, [issue.id]: "RESOLVED" }))}
                      >
                        <Check className="size-3" />
                        Resolved
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        variant={resolution === "UNRESOLVED" ? "default" : "outline"}
                        className={cn(
                          "h-6 px-2 text-[11px]",
                          resolution === "UNRESOLVED" && "bg-rose-500 text-white hover:bg-rose-400"
                        )}
                        onClick={() => setLastFailResolutions(current => ({ ...current, [issue.id]: "UNRESOLVED" }))}
                      >
                        <X className="size-3" />
                        Not resolved
                      </Button>
                    </ButtonGroup>
                  )}
                </div>
              );
            })}
            {flags.map(flag => {
              const meta = VideoProject.qaIssueCategoryMeta(flag.category);
              const focused = selectedFlagId === flag.id;
              return (
                <div
                  key={flag.id}
                  className={cn(
                    "rounded-lg border px-2 py-1.5 text-xs",
                    focused ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"
                  )}
                >
                  <div className="flex items-start gap-1">
                    <button
                      type="button"
                      className="min-w-0 flex-1 cursor-pointer text-left"
                      onClick={() => {
                        setSelectedFlagId(flag.id);
                        focusIssue(flag);
                      }}
                    >
                      <div className="flex justify-between gap-2 font-medium text-slate-800">
                        <span>{meta.label}</span>
                        <span className="text-slate-400">{formatQaTimestamp(flag.timestampMs) || "Whole cut"}</span>
                      </div>
                      {(readOnly || !focused) && flag.comment ? (
                        <p className="mt-0.5 text-slate-500">{flag.comment}</p>
                      ) : null}
                    </button>
                    {readOnly ? null : (
                      <button
                        type="button"
                        aria-label="Remove issue"
                        className="cursor-pointer rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-red-500"
                        onClick={event => {
                          event.stopPropagation();
                          removeFlag(flag.id);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    )}
                  </div>
                  {focused && !readOnly ? (
                    <Textarea
                      ref={commentRef}
                      autoSize
                      minRows={2}
                      maxRows={4}
                      value={flag.comment}
                      placeholder="What should be fixed?"
                      className="mt-1.5"
                      onChange={event =>
                        setFlags(current =>
                          current.map(item => (item.id === flag.id ? { ...item, comment: event.target.value } : item))
                        )
                      }
                      onKeyDown={event => {
                        if (event.key !== "Enter" || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
                          return;
                        }
                        event.preventDefault();
                        event.currentTarget.blur();
                        setSelectedFlagId(null);
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
            {readOnly ? (
              !lastFailIssues.length && !flags.length ? (
                <p className="text-xs text-slate-400">No QA issues on this project.</p>
              ) : null
            ) : !canFlag ? (
              <p className="text-xs text-slate-400">Switch to the edited video to flag issues.</p>
            ) : !lastFailIssues.length && !flags.length ? (
              <p className="text-xs text-slate-400">Press 1–6 to drop a pin at the playhead.</p>
            ) : !flags.length ? (
              <p className="text-xs text-slate-400">Press 1–6 to flag a new issue.</p>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-3 py-2">
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                selectedAppearance?.headerClass ?? "bg-indigo-50 text-indigo-700"
              )}
            >
              {selectedAppearance?.label ?? "Edited video"}
            </span>
            <div className="flex items-center gap-2">
              <ButtonGroup className="rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                {PLAYBACK_RATES.map(rate => (
                  <Button
                    key={rate}
                    type="button"
                    size="xs"
                    variant={playbackRate === rate ? "secondary" : "ghost"}
                    className={cn(playbackRate === rate && "bg-white text-slate-900 shadow-xs hover:bg-white")}
                    onClick={() => setPlaybackRate(rate)}
                  >
                    {rate}x
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          </div>
          {activeMedia ? (
            <div className="flex min-h-0 flex-1 overflow-hidden">
              <div
                className={cn(
                  "relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden bg-black [&_.mediaContainer]:flex [&_.mediaContainer]:h-full [&_.mediaContainer]:w-full [&_.mediaContainer]:items-center [&_.mediaContainer]:justify-center [&_.react-player]:!h-full [&_.react-player]:!w-full [&_video]:h-full [&_video]:max-h-full [&_video]:w-full [&_video]:max-w-full [&_video]:object-contain",
                  isVideoMedia(activeMedia) && "cursor-pointer [&_.mediaContainer]:pointer-events-none"
                )}
                onClick={() => {
                  if (isVideoMedia(activeMedia)) setPlayingWithCue(current => !current);
                }}
              >
                {isVideoMedia(activeMedia) ? (
                  <MediaPlayer
                    key={selectedClip.key}
                    ref={playerRef}
                    media={activeMedia}
                    playing={playing}
                    playbackRate={playbackRate}
                    controls={false}
                    objectFit="contain"
                    onPlayingChange={setPlayingWithCue}
                    onProgress={({ playedSeconds, played }) => {
                      setCurrentSeconds(playedSeconds);
                      if (selectedClip.source === "EDITED") {
                        setMaxPlayed(current => Math.max(current, played));
                      }
                    }}
                    onDuration={handleDuration}
                  />
                ) : activeMedia.uri || activeMedia.thumbnailUri ? (
                  <img
                    src={activeMedia.uri || activeMedia.thumbnailUri}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <p className="px-4 text-center text-sm text-slate-300">This file cannot be previewed.</p>
                )}
                {playbackCue ? <PlaybackCue key={playbackCue.id} playing={playbackCue.playing} /> : null}
                {skipCue ? <SkipCue key={skipCue.id} seconds={skipCue.seconds} /> : null}
              </div>
              {shouldShowTranscriptRail(activeMedia, selectedClip?.source) ? (
                <TranscriptLyrics
                  media={activeMedia}
                  source={selectedClip?.source}
                  currentSeconds={currentSeconds}
                  highlighted={spotlightRegion === "text"}
                  retrying={retryingSpoken}
                  onSeekLine={seekToTimedLine}
                  onRetrySpoken={() => void retrySpokenTranscript()}
                />
              ) : null}
            </div>
          ) : (
            <p className="flex flex-1 items-center justify-center px-4 text-center text-sm text-slate-400">
              No media is available.
            </p>
          )}
          {clips.length || extraClips.length ? (
            <MediaStrip
              clips={clips}
              lastApproved={lastApprovedClip}
              selectedKey={selectedClip?.key}
              onSelect={selectClip}
              highlighted={spotlightRegion === "source"}
            />
          ) : null}
          <div className="flex shrink-0 flex-col gap-2 border-t border-slate-200 px-3 py-2">
            <div
              className="relative cursor-pointer py-1.5"
              onClick={event => {
                if (!durationSeconds) return;
                const rect = event.currentTarget.getBoundingClientRect();
                const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
                seekTo(ratio * durationSeconds);
              }}
            >
              <div className="relative h-2 rounded-full bg-slate-200">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-indigo-500"
                  style={{ width: `${durationSeconds ? (currentSeconds / durationSeconds) * 100 : 0}%` }}
                />
                {selectedClip?.source === "EDITED"
                  ? srtCuesFromMedia(activeMedia).map((cue, index) =>
                      !durationSeconds ? null : (
                        <span
                          key={`srt-tick-${index}-${cue.startMs}`}
                          title={formatQaTimestamp(cue.startMs) || undefined}
                          className="absolute top-1/2 h-1.5 w-px -translate-x-1/2 -translate-y-1/2 bg-slate-400/70"
                          style={{ left: `${(cue.startMs / 1000 / durationSeconds) * 100}%` }}
                        />
                      )
                    )
                  : null}
                {flags
                  .filter(() => flagBelongsToClip(selectedClip))
                  .map(flag =>
                    flag.timestampMs == null || !durationSeconds ? null : (
                      <button
                        key={flag.id}
                        type="button"
                        title={`${VideoProject.qaIssueCategoryMeta(flag.category).label} ${formatQaTimestamp(
                          flag.timestampMs
                        )}`}
                        className={cn(
                          "absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full",
                          selectedFlagId === flag.id ? "bg-amber-300" : "bg-amber-500"
                        )}
                        style={{ left: `${(flag.timestampMs / 1000 / durationSeconds) * 100}%` }}
                        onClick={event => {
                          event.stopPropagation();
                          setSelectedFlagId(flag.id);
                          seekTo(flag.timestampMs! / 1000);
                        }}
                      />
                    )
                  )}
                {lastFailIssues
                  .filter(() => flagBelongsToClip(selectedClip))
                  .map(issue =>
                    issue.timestampMs == null || !durationSeconds ? null : (
                      <button
                        key={`last-fail-${issue.id}`}
                        type="button"
                        title={`Last fail · ${VideoProject.qaIssueCategoryMeta(issue.category).label} ${
                          formatQaTimestamp(issue.timestampMs) || ""
                        }`}
                        className={cn(
                          "absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full",
                          lastFailResolutions[issue.id] === "RESOLVED"
                            ? "bg-emerald-400"
                            : lastFailResolutions[issue.id] === "UNRESOLVED"
                            ? "bg-rose-400"
                            : "bg-rose-600"
                        )}
                        style={{ left: `${(issue.timestampMs / 1000 / durationSeconds) * 100}%` }}
                        onClick={event => {
                          event.stopPropagation();
                          focusIssue(issue);
                        }}
                      />
                    )
                  )}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  onClick={() => setPlayingWithCue(current => !current)}
                  disabled={!isVideoMedia(activeMedia)}
                >
                  {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
                  {formatQaTimestamp(currentSeconds * 1000) || "0:00"}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>{watchComplete ? "Watch complete" : `${Math.round(maxPlayed * 100)}% watched`}</span>
                {clips.length > 1 || (selectedClip?.source === "LAST_APPROVED" && clips.length > 0) ? (
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      const next = nextQaStudioClip(clips, selectedClip?.key);
                      if (next) selectClip(next);
                    }}
                  >
                    Next clip (I)
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-col gap-2 overflow-y-auto">
          <HudCard active={spotlightRegion === "spec"} title="Style Preferences">
            <div className="flex flex-wrap gap-1.5">
              {preferencesInfo.length ? (
                preferencesInfo.map(pref => (
                  <span
                    key={pref.key}
                    className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                      pref.key.toLowerCase().startsWith("no ")
                        ? "border-slate-200 bg-slate-50 text-slate-400"
                        : "border-indigo-100 bg-indigo-50/50 text-indigo-700"
                    )}
                  >
                    {pref.key}
                    {pref.colors?.length ? (
                      <span className="ml-1 inline-flex gap-0.5">
                        {pref.colors.map((color, index) => (
                          <span
                            key={`${color}-${index}`}
                            className="inline-block size-2.5 rounded-sm border border-slate-200"
                            style={{ backgroundColor: formatHexColor(color) || color }}
                          />
                        ))}
                      </span>
                    ) : null}
                  </span>
                ))
              ) : (
                <span className="text-slate-400">No stored preferences</span>
              )}
            </div>
          </HudCard>
          <HudCard active={spotlightRegion === "text" || spotlightRegion === "openClose"} title="Player Info">
            {playerInfoRows.length ? (
              <dl className="space-y-1.5">
                {playerInfoRows.map(row => (
                  <div key={row.label} className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {row.label}
                    </dt>
                    <dd className="m-0 min-w-0 text-right text-slate-700 wrap-break-word">{row.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-slate-400">No Player details loaded</p>
            )}
          </HudCard>
          <HudCard active={spotlightRegion === "post"} title="Post caption & compliance">
            <p className="whitespace-pre-wrap text-slate-700">{caption || "No caption"}</p>
            <p className="mt-1 text-[11px] text-slate-400">
              Compliance {videoProject.allowCompliance ? "on" : "off"} · Auto-post{" "}
              {videoProject.isAutoPost ? "on" : "off"}
              {videoProject.preferredPostDate ? ` · ${videoProject.preferredPostDate}` : ""}
            </p>
            {blockedWordsInCaption ? (
              <p className="mt-1 text-[11px] font-medium text-amber-700">Blocked words: {blockedWordsInCaption}</p>
            ) : null}
          </HudCard>
          <HudCard active={spotlightRegion === "notes"} title="Notes">
            {hasReadableNotes(playerNotes, coachNotes) ? (
              <div className="space-y-3 text-slate-700">
                {playerNotes?.notes?.trim() ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Player</p>
                    <LinkifiedText>{playerNotes.notes}</LinkifiedText>
                  </div>
                ) : null}
                {coachNotes?.notes?.trim() ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Coach</p>
                    <LinkifiedText>{coachNotes.notes}</LinkifiedText>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-slate-400">No notes</p>
            )}
          </HudCard>
          <HudCard
            active={spotlightRegion === "notes"}
            title="Messages"
            action={
              videoProjectMessages?.length ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                  <MessageSquare className="size-3" />
                  {videoProjectMessages.length}
                </span>
              ) : null
            }
          >
            {videoProjectMessages?.length ? (
              <div className="space-y-2 text-slate-700">
                {videoProjectMessages.map(message => (
                  <div key={message.id} className="space-y-0.5">
                    <p className="text-[10px] font-semibold text-slate-400">
                      {message.userName || message.userRole || "Message"}
                      {message.createdDate ? ` · ${printDateToChat(message.createdDate)}` : ""}
                    </p>
                    <LinkifiedText>{message.content || ""}</LinkifiedText>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">No messages</p>
            )}
          </HudCard>
        </div>
      </Dialog.Content>

      <Dialog.Footer className="shrink-0 sm:items-center sm:justify-between">
        <button
          type="button"
          className="hidden flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 hover:text-slate-800 sm:flex"
          onClick={() => setShowShortcuts(current => !current)}
        >
          <Keyboard className="size-3.5" />
          <ShortcutHint chords={[["Space"]]} label="play" />
          {readOnly ? null : <ShortcutHint chords={[["1"], ["6"]]} range label="flag" />}
          <ShortcutHint chords={[["I"]]} label="next clip" />
          {readOnly ? null : <ShortcutHint chords={[[modKey, "Enter"]]} label={hasIssues ? "send back" : "approve"} />}
          <ShortcutHint chords={[["?"]]} label="shortcuts" />
        </button>
        {readOnly ? (
          <p className="text-xs font-medium text-slate-500">
            View only — flagging and decisions are disabled until QA review is in progress.
          </p>
        ) : (
          <Button
            variant={watchComplete ? "default" : "outline"}
            onClick={() => void handleSubmit()}
            disabled={saving}
            loading={saving}
          >
            {hasIssues ? <RotateCcw /> : <CircleCheck />}
            {hasIssues ? "Send back for edits" : "Send to client"}
            <KbdGroup>
              <Kbd>{modKey}</Kbd>
              <Kbd>Enter</Kbd>
            </KbdGroup>
          </Button>
        )}
      </Dialog.Footer>

      {showShortcuts ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 p-6">
          <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <strong className="text-slate-800">Shortcuts</strong>
              <Button type="button" size="xs" variant="ghost" onClick={() => setShowShortcuts(false)}>
                Close
              </Button>
            </div>
            <ul className="grid grid-cols-1 gap-2 text-slate-600 sm:grid-cols-2">
              <ShortcutRow label="Play/pause">
                <Kbd>Space</Kbd>
                <Kbd>K</Kbd>
              </ShortcutRow>
              <ShortcutRow label="Skip 2s">
                <Kbd>J</Kbd>
                <Kbd>L</Kbd>
                <Kbd>←</Kbd>
                <Kbd>→</Kbd>
              </ShortcutRow>
              <ShortcutRow label="Next clip">
                <Kbd>I</Kbd>
              </ShortcutRow>
              {readOnly ? null : (
                <>
                  <ShortcutRow label="Flag at playhead">
                    <Kbd>1</Kbd>
                    <span>–</span>
                    <Kbd>6</Kbd>
                  </ShortcutRow>
                  <ShortcutRow label="Remove flag">
                    <Kbd>Backspace</Kbd>
                  </ShortcutRow>
                  <ShortcutRow label="Send back / approve">
                    <KbdGroup>
                      <Kbd>{modKey}</Kbd>
                      <Kbd>Enter</Kbd>
                    </KbdGroup>
                  </ShortcutRow>
                </>
              )}
            </ul>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

function ShortcutHint({ chords, label, range }: { chords: string[][]; label: string; range?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {chords.map((keys, index) => (
        <React.Fragment key={`${keys.join("-")}-${index}`}>
          {index > 0 ? <span>{range ? "–" : "/"}</span> : null}
          <ShortcutChord keys={keys} />
        </React.Fragment>
      ))}
      {label}
    </span>
  );
}

function ShortcutChord({ keys }: { keys: string[] }) {
  if (keys.length === 1) {
    return <Kbd>{keys[0]}</Kbd>;
  }
  return (
    <KbdGroup>
      {keys.map(key => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </KbdGroup>
  );
}

function ShortcutRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span>{label}</span>
      <span className="inline-flex items-center gap-1">{children}</span>
    </li>
  );
}

function HudCard({
  title,
  active,
  action,
  children,
}: {
  title: string;
  active?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border p-3 text-xs",
        active ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50/50"
      )}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function LinkifiedText({ children }: { children: string }) {
  return (
    <span className="whitespace-pre-wrap wrap-break-word">
      <Linkify
        componentDecorator={(href, text, key) => {
          const truncatedText = text.length > 30 ? `${text.substring(0, 27)}...` : text;
          return (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={text}
              className="text-primary hover:underline"
            >
              {truncatedText}
            </a>
          );
        }}
      >
        {children}
      </Linkify>
    </span>
  );
}

function FadeCue({ align = "center", children }: { align?: "left" | "center" | "right"; children: React.ReactNode }) {
  const [phase, setPhase] = React.useState<"start" | "shown" | "gone">("start");

  React.useEffect(() => {
    const show = window.requestAnimationFrame(() => setPhase("shown"));
    const hide = window.setTimeout(() => setPhase("gone"), 350);
    return () => {
      window.cancelAnimationFrame(show);
      window.clearTimeout(hide);
    };
  }, []);

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 z-10 flex items-center",
        align === "left" && "justify-start pl-[12%]",
        align === "center" && "justify-center",
        align === "right" && "justify-end pr-[12%]"
      )}
    >
      <div
        className={cn(
          "transition-all ease-out",
          phase === "start" && "scale-75 opacity-0 duration-150",
          phase === "shown" && "scale-100 opacity-100 duration-150",
          phase === "gone" && "scale-125 opacity-0 duration-400"
        )}
      >
        {children}
      </div>
    </div>
  );
}

function TranscriptLyrics({
  media,
  source,
  currentSeconds,
  highlighted,
  retrying,
  onSeekLine,
  onRetrySpoken,
}: {
  media?: Media;
  source?: QaStudioSource;
  currentSeconds: number;
  highlighted?: boolean;
  retrying?: boolean;
  onSeekLine: (line: TimedLine) => void;
  onRetrySpoken: () => void;
}) {
  const showOnScreen = shouldShowOnScreenStack(media, source);
  const showSpoken = shouldShowSpokenStack(media);
  const onScreenLines = srtCuesFromMedia(media);
  const spoken = spokenLinesFromMedia(media);
  const generating = shouldPollTranscriptStatus(media);
  const failed = media?.transcriptStatus === "FAILED";
  const comparisonRows = shouldShowTranscriptComparison(media, source) ? transcriptComparisonFromMedia(media) : [];
  const comparing = comparisonRows.length > 0;
  const wordingDiff = comparing
    ? comparisonDifferenceCount(comparisonRows)
    : onScreenLines.length && spoken.lines.length
    ? wordingDifferenceCount(
        onScreenLines.map(line => line.text).join(" "),
        spoken.lines.map(line => line.text).join(" ")
      )
    : null;
  const onScreenActiveIndex = activeTimedLineIndex(onScreenLines, currentSeconds * 1000);
  const spokenActiveIndex = activeTimedLineIndex(spoken.lines, currentSeconds * 1000);
  const comparisonActiveIndex = activeComparisonRowIndex(comparisonRows, currentSeconds * 1000);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const onScreenActiveRef = React.useRef<HTMLButtonElement>(null);
  const spokenActiveRef = React.useRef<HTMLButtonElement>(null);
  const comparisonActiveRef = React.useRef<HTMLButtonElement>(null);
  const followPlayheadRef = React.useRef(true);
  const followStackRef = React.useRef<"on-screen" | "spoken" | "comparison">(
    comparing ? "comparison" : showOnScreen ? "on-screen" : "spoken"
  );
  const programmaticUntilRef = React.useRef(0);

  const centerActiveLine = React.useCallback((element?: HTMLElement | null) => {
    const container = scrollRef.current;
    const target =
      element ||
      (followStackRef.current === "comparison"
        ? comparisonActiveRef.current
        : followStackRef.current === "spoken"
        ? spokenActiveRef.current
        : onScreenActiveRef.current) ||
      comparisonActiveRef.current ||
      onScreenActiveRef.current ||
      spokenActiveRef.current;
    if (!container || !target || !followPlayheadRef.current) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = clampScrollTop(
      scrollTopToCenterElement(
        container.scrollTop,
        containerRect.top,
        container.clientHeight,
        targetRect.top,
        targetRect.height
      ),
      container.scrollHeight,
      container.clientHeight
    );
    programmaticUntilRef.current = Date.now() + 500;
    container.scrollTo({ top: nextTop, behavior: "smooth" });
  }, []);

  const seekFromStack = React.useCallback(
    (stack: "on-screen" | "spoken" | "comparison", line: TimedLine) => {
      followPlayheadRef.current = true;
      followStackRef.current = stack;
      onSeekLine(line);
      window.requestAnimationFrame(() => centerActiveLine());
    },
    [centerActiveLine, onSeekLine]
  );

  React.useEffect(() => {
    followPlayheadRef.current = true;
    followStackRef.current = comparing ? "comparison" : showOnScreen ? "on-screen" : "spoken";
  }, [comparing, media?.id, showOnScreen]);

  React.useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const pauseFollowFromScroll = () => {
      if (Date.now() < programmaticUntilRef.current) return;
      followPlayheadRef.current = false;
    };
    const pauseFollowFromPointer = () => {
      followPlayheadRef.current = false;
      programmaticUntilRef.current = 0;
    };
    container.addEventListener("wheel", pauseFollowFromPointer, { passive: true });
    container.addEventListener("touchmove", pauseFollowFromPointer, { passive: true });
    container.addEventListener("scroll", pauseFollowFromScroll, { passive: true });
    return () => {
      container.removeEventListener("wheel", pauseFollowFromPointer);
      container.removeEventListener("touchmove", pauseFollowFromPointer);
      container.removeEventListener("scroll", pauseFollowFromScroll);
    };
  }, []);

  React.useEffect(() => {
    if (!followPlayheadRef.current) return;
    centerActiveLine();
  }, [centerActiveLine, comparisonActiveIndex, onScreenActiveIndex, spokenActiveIndex]);

  return (
    <aside
      className={cn(
        "flex shrink-0 flex-col overflow-hidden border-l",
        comparing ? "w-[min(38rem,52%)]" : "w-[min(22rem,40%)]",
        highlighted ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50"
      )}
    >
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <Captions className="size-3.5" />
          Transcript
        </div>
        {comparing && wordingDiff != null ? (
          <p className="text-[11px] text-slate-500">
            {wordingDiff} wording difference{wordingDiff === 1 ? "" : "s"}
          </p>
        ) : null}
      </div>
      {comparing ? (
        <div className="grid grid-cols-2 gap-3 px-5 pb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          <span>On-screen</span>
          <span>Spoken</span>
        </div>
      ) : null}
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto px-5 pt-2 pb-8 [mask-image:linear-gradient(to_bottom,transparent,black_1.5rem,black_calc(100%-2rem),transparent)]"
      >
        {comparing ? (
          <TranscriptComparisonRows
            rows={comparisonRows}
            activeIndex={comparisonActiveIndex}
            activeRef={comparisonActiveRef}
            currentSeconds={currentSeconds}
            onSeekLine={line => seekFromStack("comparison", line)}
          />
        ) : (
          <div className="flex flex-col gap-6">
            {showOnScreen ? (
              <section className="flex flex-col gap-3">
                <h4 className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">On-screen</h4>
                {onScreenLines.length ? (
                  <TimedLyricLines
                    lines={onScreenLines}
                    activeIndex={onScreenActiveIndex}
                    activeRef={onScreenActiveRef}
                    preserveLineBreaks
                    onSeekLine={line => seekFromStack("on-screen", line)}
                  />
                ) : (
                  <p className="text-sm text-slate-500">No on-screen captions</p>
                )}
              </section>
            ) : null}
            {showSpoken ? (
              <section className="flex flex-col gap-3">
                <h4 className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Spoken</h4>
                {failed ? (
                  <div className="flex flex-col items-start gap-2">
                    <p className="text-sm text-slate-500">Spoken transcript failed — retry</p>
                    <Button type="button" size="xs" variant="outline" loading={retrying} onClick={onRetrySpoken}>
                      Retry
                    </Button>
                  </div>
                ) : generating && !spoken.lines.length && !spoken.untimed.length ? (
                  <p className="text-sm text-slate-500">Generating spoken transcript…</p>
                ) : spoken.lines.length ? (
                  <TimedLyricLines
                    lines={spoken.lines}
                    activeIndex={spokenActiveIndex}
                    activeRef={spokenActiveRef}
                    currentSeconds={currentSeconds}
                    onSeekLine={line => seekFromStack("spoken", line)}
                  />
                ) : spoken.untimed.length ? (
                  <div className="flex flex-col gap-4">
                    {spoken.untimed.map((line, index) => (
                      <p key={`${index}-${line.slice(0, 24)}`} className="text-lg leading-8 font-medium text-slate-700">
                        {line}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No spoken transcript</p>
                )}
              </section>
            ) : null}
            {wordingDiff != null ? (
              <p className="text-[11px] text-slate-500">
                {wordingDiff} wording difference{wordingDiff === 1 ? "" : "s"}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </aside>
  );
}

function TranscriptComparisonRows({
  rows,
  activeIndex,
  activeRef,
  currentSeconds,
  onSeekLine,
}: {
  rows: TranscriptComparisonRow[];
  activeIndex: number;
  activeRef: React.Ref<HTMLButtonElement>;
  currentSeconds: number;
  onSeekLine: (line: TimedLine) => void;
}) {
  const currentMs = currentSeconds * 1000;
  return (
    <div className="flex flex-col gap-3">
      {rows.map((row, index) => {
        const active = index === activeIndex;
        const timed = comparisonRowAsTimedLine(row);
        const className = cn(
          "grid w-full grid-cols-2 gap-3 py-1.5 text-left text-lg leading-7 font-medium transition-colors",
          timed && "cursor-pointer",
          active ? "text-slate-900" : "text-slate-400",
          timed && !active && "hover:text-indigo-600",
          active && "border-l-2 border-amber-400 pl-2"
        );
        const body = (
          <>
            <DiffCell parts={row.onScreenParts} empty={!row.onScreenText} />
            <DiffCell
              parts={row.spokenParts}
              empty={!row.spokenText}
              currentMs={currentMs}
              onSeekPart={part => {
                const line = timedPartAsTimedLine(part);
                if (line) onSeekLine(line);
              }}
            />
          </>
        );
        if (!timed) {
          return (
            <div key={`${index}-${(row.spokenText || "").slice(0, 24)}`} className={className}>
              {body}
            </div>
          );
        }
        return (
          <button
            key={`${index}-${row.startMs}-${(row.onScreenText || row.spokenText || "").slice(0, 24)}`}
            ref={active ? activeRef : undefined}
            type="button"
            title={formatQaTimestamp(row.startMs) || undefined}
            className={className}
            onClick={() => onSeekLine(timed)}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}

function spokenWordClass(active?: boolean) {
  return active
    ? "rounded-sm bg-amber-200 text-slate-900 underline decoration-amber-500 underline-offset-4"
    : undefined;
}

function DiffCell({
  parts,
  empty,
  currentMs,
  onSeekPart,
}: {
  parts: DiffPart[];
  empty?: boolean;
  currentMs?: number;
  onSeekPart?: (part: DiffPart) => void;
}) {
  if (empty || !parts.length) {
    return <span className="text-slate-300">—</span>;
  }
  const activePartIndex = currentMs == null ? -1 : activeTimedPartIndex(parts, currentMs);
  return (
    <span className="min-w-0 whitespace-pre-wrap">
      {parts.map((part, index) => {
        const timed = part.startMs != null;
        return (
          <React.Fragment key={`${index}-${part.text}`}>
            {index > 0 ? " " : null}
            <span
              className={cn(
                timed && "cursor-pointer",
                part.kind === "remove" && index !== activePartIndex && "rounded-sm bg-red-100 text-red-700",
                part.kind === "add" && index !== activePartIndex && "rounded-sm bg-emerald-100 text-emerald-700",
                spokenWordClass(index === activePartIndex)
              )}
              onClick={
                timed && onSeekPart
                  ? event => {
                      event.stopPropagation();
                      onSeekPart(part);
                    }
                  : undefined
              }
            >
              {part.text}
            </span>
          </React.Fragment>
        );
      })}
    </span>
  );
}

function TimedLyricLines({
  lines,
  activeIndex,
  activeRef,
  preserveLineBreaks,
  currentSeconds,
  onSeekLine,
}: {
  lines: TimedLine[];
  activeIndex: number;
  activeRef: React.Ref<HTMLButtonElement>;
  preserveLineBreaks?: boolean;
  currentSeconds?: number;
  onSeekLine: (line: TimedLine) => void;
}) {
  const currentMs = (currentSeconds ?? 0) * 1000;
  return (
    <div className="flex flex-col gap-4">
      {lines.map((line, index) => {
        const active = index === activeIndex;
        const spokenWords = line.words?.filter(word => word.word);
        const activeWordIndex = spokenWords?.length ? activeTimedPartIndex(spokenWords, currentMs) : -1;
        return (
          <button
            key={`${index}-${line.startMs}-${line.text.slice(0, 24)}`}
            ref={active ? activeRef : undefined}
            type="button"
            title={formatQaTimestamp(line.startMs) || undefined}
            className={cn(
              "cursor-pointer text-left text-lg leading-8 font-medium transition-colors",
              preserveLineBreaks && "whitespace-pre-wrap",
              active ? "text-slate-900" : "text-slate-400 hover:text-indigo-600",
              active && "border-l-2 border-amber-400 pl-2"
            )}
            onClick={() => onSeekLine(line)}
          >
            {spokenWords?.length
              ? spokenWords.map((word, wordIndex) => (
                  <React.Fragment key={`${wordIndex}-${word.startMs}-${word.word}`}>
                    {wordIndex > 0 ? " " : null}
                    <span
                      className={cn("cursor-pointer", spokenWordClass(wordIndex === activeWordIndex))}
                      onClick={event => {
                        event.stopPropagation();
                        onSeekLine({ startMs: word.startMs, endMs: word.endMs, text: word.word });
                      }}
                    >
                      {word.word}
                    </span>
                  </React.Fragment>
                ))
              : line.text}
          </button>
        );
      })}
    </div>
  );
}

function PlaybackCue({ playing }: { playing: boolean }) {
  const Icon = playing ? Play : Pause;
  return (
    <FadeCue>
      <div className="flex size-16 items-center justify-center rounded-full bg-black/55 text-white shadow-lg">
        <Icon className="size-8 fill-white" />
      </div>
    </FadeCue>
  );
}

function SkipCue({ seconds }: { seconds: number }) {
  const shown = Math.round(seconds);
  if (shown === 0) return null;
  const rewind = shown < 0;
  const label = rewind ? `${shown}` : `+${shown}`;
  const Icon = rewind ? Rewind : FastForward;
  return (
    <FadeCue align={rewind ? "left" : "right"}>
      <div className="flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-2.5 text-white shadow-lg">
        {rewind ? <Icon className="size-5 fill-white" /> : null}
        <span className="text-lg font-semibold tracking-tight">{label}</span>
        {rewind ? null : <Icon className="size-5 fill-white" />}
      </div>
    </FadeCue>
  );
}

function clipAppearance(source: QaStudioSource) {
  if (source === "LAST_APPROVED") {
    return {
      label: "Last approved (other project)",
      badge: "Prior",
      headerClass: "bg-amber-100 text-amber-800",
      idleBorder: "border-amber-300 hover:border-amber-500",
      badgeClass: "bg-amber-500 text-white",
    };
  }
  if (source === "ORIGINAL") {
    return {
      label: "Original file",
      badge: "Orig",
      headerClass: "bg-slate-100 text-slate-700",
      idleBorder: "border-slate-300 hover:border-slate-400",
      badgeClass: "bg-slate-600 text-white",
    };
  }
  return {
    label: "Edited video",
    badge: "Edit",
    headerClass: "bg-indigo-50 text-indigo-700",
    idleBorder: "border-indigo-300 hover:border-indigo-400",
    badgeClass: "bg-indigo-500 text-white",
  };
}

function MediaStrip({
  clips,
  lastApproved,
  selectedKey,
  onSelect,
  highlighted,
}: {
  clips: QaStudioClip[];
  lastApproved?: QaStudioClip;
  selectedKey?: string;
  onSelect: (clip: QaStudioClip) => void;
  highlighted?: boolean;
}) {
  const selectedRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [selectedKey]);

  return (
    <div
      className={cn(
        "shrink-0 border-t px-3 py-2",
        highlighted ? "border-amber-400 bg-amber-50 ring-2 ring-inset ring-amber-400" : "border-slate-200 bg-slate-50"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
          {clips.map((clip, index) => {
            const previous = clips[index - 1];
            return (
              <React.Fragment key={clip.key}>
                {previous && previous.source !== clip.source ? (
                  <div className="mx-0.5 h-14 w-px shrink-0 bg-slate-200" aria-hidden />
                ) : null}
                <StripThumb
                  clip={clip}
                  selected={clip.key === selectedKey}
                  selectedRef={clip.key === selectedKey ? selectedRef : undefined}
                  onSelect={onSelect}
                />
              </React.Fragment>
            );
          })}
        </div>
        {lastApproved ? (
          <div className="flex shrink-0 items-center gap-2 border-l border-dashed border-slate-300 pl-3">
            <span className="hidden max-w-[4.75rem] text-[9px] leading-tight font-semibold uppercase tracking-wide text-amber-700 sm:block">
              Other project
            </span>
            <StripThumb
              clip={lastApproved}
              selected={lastApproved.key === selectedKey}
              selectedRef={lastApproved.key === selectedKey ? selectedRef : undefined}
              onSelect={onSelect}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StripThumb({
  clip,
  selected,
  selectedRef,
  onSelect,
}: {
  clip: QaStudioClip;
  selected: boolean;
  selectedRef?: React.Ref<HTMLButtonElement>;
  onSelect: (clip: QaStudioClip) => void;
}) {
  const appearance = clipAppearance(clip.source);
  return (
    <button
      ref={selectedRef}
      type="button"
      title={appearance.label}
      className={cn(
        "relative h-16 w-16 shrink-0 overflow-hidden rounded-md border-2 bg-slate-100",
        selected ? "border-indigo-500 ring-2 ring-indigo-200" : appearance.idleBorder
      )}
      onClick={() => onSelect(clip)}
    >
      <ClipThumb media={clip.media} />
      <span
        className={cn(
          "absolute inset-x-0 bottom-0 px-1 py-0.5 text-center text-[9px] font-semibold uppercase tracking-wide",
          appearance.badgeClass
        )}
      >
        {appearance.badge}
      </span>
    </button>
  );
}

function ClipThumb({ media }: { media: Media }) {
  if (media.thumbnailUri) {
    return <img src={media.thumbnailUri} alt="" className="h-full w-full object-cover" />;
  }
  if (isVideoMedia(media) && media.uri) {
    return (
      <video src={`${media.uri}#t=0.001`} className="h-full w-full object-cover" muted playsInline preload="metadata" />
    );
  }
  if (media.uri) {
    return <img src={media.uri} alt="" className="h-full w-full object-cover" />;
  }
  return <FolderOpen className="m-auto size-4 text-slate-400" />;
}
